import { Injectable, NgZone } from '@angular/core';
import {
  defaultSpeechRecognitionConfig,
  SpeechRecognitionConfig,
  SpeechRecognitionService,
  SpeechResult,
} from 'src/app/services/speech-recognition.service';
import { captureException } from '@sentry/angular';
import { FullStory } from '@fullstory/browser';
import { filter, switchMap, combineLatest, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Collaboration } from '@pncl/common-models';
import { User } from '../models/user';
import { ActiveSpeaker, TrackType } from '../common/interfaces/rtc-interface';
import { TranscriptRepository } from '../state/transcript.repository';
import { SpaceRepository } from '../state/space.repository';
import { SpeechService } from '../models/session';
import { modifiedSetInterval } from '../utilities/ZoneUtils';
import { AnalyticsService } from './analytics.service';
import { UserService } from './user.service';
import { ProviderStateService } from './provider-state.service';
import { VideoCallTracksStateService } from './video-call-tracks-state.service';
import { FLAGS, FlagsService } from './flags.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { SpacesService } from './spaces.service';
import { RtcServiceController } from './rtc.service';
import Transcription = Collaboration.Transcription;

export interface TranscriptMetadata {
  averageMicrophoneGain: number;
  blueBorderActivePercentage: number;
  dailyActivePercentage: number;
  webSpeechSoundEndTimestamp: Date;
  webspeechSoundStartTimestamp: Date;
  webSpeechSpeechEndTimestamo: Date;
  webspeechSpeechStartTimestamp: Date;
  dailySpeakerEchoDetected: boolean;
  feLrPrediction: boolean;
  feLrPredictionActual: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SpacesTranscriptionsService {
  private user?: User;
  private spaceId?: string;
  private breakoutRoomId?: string;

  private isTranscriptionLoggingEnabled = false;
  private isBreakoutRoomsEnabled = false;

  private audioTrack: MediaStreamTrack | undefined;
  private currentActiveSpeaker?: ActiveSpeaker;
  private gainTimeInterval?: NodeJS.Timer;
  private activeSpeakerInterval?: NodeJS.Timer;
  private activeSpeakerTracker = new Array<number>();
  private activeSpeakerBlueBorderTracker = new Array<number>();
  private userSpeechGainValues = new Array<number>();
  private webspeechSoundStartTimestamp = new Date();
  private webSpeechSoundEndTimestamp = new Date();
  private webspeechSpeechStartTimestamp = new Date();
  private webSpeechSpeechEndTimestamo = new Date();
  private averageMicrophoneGain = 0;
  private blueBorderActivePercentage = 0;
  private dailyActivePercentage = 0;

  private currentSpeechService: SpeechService | null = null;

  private deepgramOnResultSubscription?: Subscription;
  private speechApiOnResultSubscription?: Subscription;

  constructor(
    private speechRecognitionService: SpeechRecognitionService,
    private transcriptRepo: TranscriptRepository,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private providerState: ProviderStateService,
    private videoCallTracksStateService: VideoCallTracksStateService,
    private spaceService: SpacesService,
    private spaceRepo: SpaceRepository,
    private sessionSharedDataService: SessionSharedDataService,
    private flagsService: FlagsService,
    private rtcServiceController: RtcServiceController,
    private ngZone: NgZone,
  ) {
    this.setFeatureFlags();
    this.listenToActiveSpaceRoomChange();
    this.listenToCurrentUserChange();
    this.listenToRtcActiveSpeaker();
  }

  public init(service: SpeechService | null): void {
    if (service) {
      this.start(service);
    } else {
      if (this.currentSpeechService) {
        this.stop(this.currentSpeechService);
      }
    }

    this.currentSpeechService = service;
  }

  public handleSpaceTranscriptEvent(
    data: Transcription.SpaceTranscriptItem,
    operation: string,
    activeUsers: Array<User>,
    inactiveUsers: Array<User>,
  ): void {
    switch (operation) {
      case 'add':
        this.handleAddTranscriptMessage(data, activeUsers, inactiveUsers);
        break;
      case 'edit':
        this.handleEditTranscriptMessage(data);
        break;
      case 'delete':
        this.handleDeleteTranscriptMessage(data);
        break;
      default:
        break;
    }
  }

  public dispose(speechServiceType: SpeechService) {
    switch (speechServiceType) {
      case SpeechService.DEEPGRAM:
        this.rtcServiceController.service.stopTranscription();
        this.deepgramOnResultSubscription?.unsubscribe();
        FullStory('trackEvent', {
          name: 'Transcription stopped',
          properties: { speechService: speechServiceType },
        });
        break;

      case SpeechService.WEB_SPEECH_API:
        this.speechRecognitionService.abortSpeechRecognition();
        this.speechApiOnResultSubscription?.unsubscribe();
        FullStory('trackEvent', {
          name: 'Transcription stopped',
          properties: { speechService: speechServiceType },
        });
        break;

      default:
        break;
    }
  }

  private start(service: SpeechService): void {
    switch (service) {
      case SpeechService.DEEPGRAM:
        this.startDeepgram();
        FullStory('trackEvent', {
          name: 'Transcription started',
          properties: { speechService: service },
        });
        break;

      case SpeechService.WEB_SPEECH_API:
        this.startSpeechRecognition();
        FullStory('trackEvent', {
          name: 'Transcription started',
          properties: { speechService: service },
        });
        break;

      default:
        break;
    }
  }

  private stop(service: SpeechService): void {
    this.dispose(service);
  }

  private handleAddTranscriptMessage(
    data: Transcription.SpaceTranscriptItem,
    activeUsers: Array<User>,
    inactiveUsers: Array<User>,
  ): void {
    const activeSpeakerData = activeUsers.find((x) => x._id === data.speakerId);
    const inactiveSpeakerData = inactiveUsers.find((x) => x._id === data.speakerId);
    const speaker = activeSpeakerData || inactiveSpeakerData || undefined;
    const contextType =
      speaker?._id === this.user?._id
        ? Transcription.ChatContextType.sender
        : Transcription.ChatContextType.receiver;
    this.transcriptRepo.addSpaceTranscriptItem({
      ...data,
      speaker: speaker,
      contextType: contextType,
    });
  }

  private handleEditTranscriptMessage(data: Transcription.SpaceTranscriptItem): void {
    if (data.speaker?._id !== this.user?._id) {
      const currentTranscriptItem = this.transcriptRepo.getTranscriptItem(data._id);
      if (currentTranscriptItem) {
        this.transcriptRepo.editTranscriptItem((x) => x._id === data._id, {
          transcript: data.transcript,
        });
      }
    }
  }

  private handleDeleteTranscriptMessage(data: Transcription.SpaceTranscriptItem): void {
    if (data.speaker?._id !== this.user?._id) {
      const currentTranscriptItem = this.transcriptRepo.getTranscriptItem(data._id);
      if (currentTranscriptItem) {
        this.transcriptRepo.deleteTranscriptItem((x) => x._id === data._id);
      }
    }
  }

  private listenToActiveSpaceRoomChange() {
    combineLatest([this.spaceRepo.activeSpace$, this.spaceRepo.activeSpaceCurrentRoomUid$])
      .pipe(untilDestroyed(this))
      .subscribe(([activeSpace, activeSpaceCurrentRoomUid]) => {
        if (!activeSpace) {
          return;
        }
        if (this.isBreakoutRoomsEnabled && !activeSpaceCurrentRoomUid) {
          return;
        }
        this.spaceId = activeSpace?._id;
        this.breakoutRoomId = activeSpaceCurrentRoomUid;
      });
  }

  private startDeepgram() {
    this.rtcServiceController.service.startTranscription();
    this.deepgramOnResultSubscription = this.listenToTranscriptResult();

    // when user start deepgram, we want to make sure all space participants are connected to deepgram as well
    // the in the following code a request to pencil-api to publish and save the speech service to 'DEEPGRAM'
    if (this.spaceId) {
      this.spaceService
        .setSpaceTranscriptServiceType(this.spaceId, SpeechService.DEEPGRAM)
        .pipe(untilDestroyed(this))
        .subscribe();
    }
  }

  private listenToTranscriptResult(): Subscription {
    return this.providerState.transcriptMessage$.pipe(untilDestroyed(this)).subscribe((result) => {
      if (result.is_final && result.user_id === this.user?._id) {
        const metadata = {
          averageMicrophoneGain: this.averageMicrophoneGain,
          blueBorderActivePercentage: this.blueBorderActivePercentage,
          dailyActivePercentage: this.dailyActivePercentage,
          webSpeechSoundEndTimestamp: this.webSpeechSoundEndTimestamp,
          webspeechSoundStartTimestamp: this.webspeechSoundStartTimestamp,
          webSpeechSpeechEndTimestamo: this.webSpeechSpeechEndTimestamo,
          webspeechSpeechStartTimestamp: this.webspeechSpeechStartTimestamp,
          dailySpeakerEchoDetected: false,
          feLrPrediction: true,
          feLrPredictionActual: 1,
        };

        if (this.spaceId) {
          this.saveTranscript(
            this.spaceId,
            result.text,
            'en',
            metadata,
            this.analyticsService.getCurrentSessionId(),
            this.breakoutRoomId,
          );
        }
      }
    });
  }

  private startSpeechRecognition() {
    const config: SpeechRecognitionConfig = {
      ...defaultSpeechRecognitionConfig,
      lang: this.user?.settings?.lang_interface
        ? this.user.settings.lang_interface
        : defaultSpeechRecognitionConfig.lang,
      onresult: this.onSpeechResult.bind(this),
      onspeechstart: this.onSpeechStart.bind(this),
      onspeechend: this.onSpeechEnd.bind(this),
      onsoundstart: this.onSoundStart.bind(this),
      onsoundend: this.onSoundEnd.bind(this),
      onerror: this.onSpeechApiError.bind(this),
    };
    this.speechRecognitionService.initSpeechRecognition(config);

    // we listen to localParticipantId once we have the value we will use it get form
    // the participant tracks map as audioTrack$
    // listening audioTrack changes we can know
    //  when: start and stop web-speech-api service
    this.speechApiOnResultSubscription = this.providerState.localParticipantId$
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        switchMap((localParticipantId) =>
          this.videoCallTracksStateService.getPariticpantTrackObservable(
            localParticipantId,
            TrackType.VIDEO,
            this,
            true,
          ),
        ),
      )
      .subscribe((audioTrack) => {
        if (!audioTrack) {
          this.speechRecognitionService.stopSpeechRecognition();
        }
        if (audioTrack) {
          this.audioTrack = audioTrack;
          this.speechRecognitionService.startSpeechRecognition({ autoStart: true });
        }
      });
  }

  private onSpeechApiError(event): void {
    captureException(event.error, { extra: { message: event.message } });
  }

  private onSpeechResult(result: SpeechResult) {
    if (result.isFinal) {
      const isLocalParticipantSpeaker =
        this.currentActiveSpeaker?.speakerId === this.providerState.localParticipantIdValue &&
        this.currentActiveSpeaker.timestmap < result.timestamp;

      const lrPredictionInput = {
        'time-active-bb-pct': this.blueBorderActivePercentage,
        'time-active-daily-pct': this.dailyActivePercentage,
        'avg-mic-gain': this.averageMicrophoneGain,
        'echo-detected': isLocalParticipantSpeaker ? 0 : 1,
        'sound-duration':
          (this.webSpeechSoundEndTimestamp.getTime() -
            this.webspeechSoundStartTimestamp.getTime()) /
          1000,
        'speech-duration':
          (this.webSpeechSpeechEndTimestamo.getTime() -
            this.webspeechSpeechStartTimestamp.getTime()) /
          1000,
        'speech-start-delay':
          (this.webspeechSpeechStartTimestamp.getTime() -
            this.webspeechSoundStartTimestamp.getTime()) /
          1000,
        'transcript-length': result.transcript.split(' ').length,
      };

      const lrPredictionResult = this.sendTranscriptInput(lrPredictionInput);

      const metadata = {
        averageMicrophoneGain: this.averageMicrophoneGain,
        blueBorderActivePercentage: this.blueBorderActivePercentage,
        dailyActivePercentage: this.dailyActivePercentage,
        webSpeechSoundEndTimestamp: this.webSpeechSoundEndTimestamp,
        webspeechSoundStartTimestamp: this.webspeechSoundStartTimestamp,
        webSpeechSpeechEndTimestamo: this.webSpeechSpeechEndTimestamo,
        webspeechSpeechStartTimestamp: this.webspeechSpeechStartTimestamp,
        dailySpeakerEchoDetected: !isLocalParticipantSpeaker,
        feLrPrediction: lrPredictionResult >= 0.5,
        feLrPredictionActual: lrPredictionResult,
      };

      if (lrPredictionResult || this.isTranscriptionLoggingEnabled) {
        if (this.spaceId) {
          this.saveTranscript(
            this.spaceId,
            result.transcript,
            result.lang,
            metadata,
            this.analyticsService.getCurrentSessionId(),
            this.breakoutRoomId,
          );
        } else {
          captureException('Cannot save transcript item with invalid space id');
        }
      }
    }
  }

  private saveTranscript(
    spaceId: string,
    transcript: string,
    lang: string,
    metadata: TranscriptMetadata,
    sessionId?: string,
    breakoutRoomId?: string,
  ): void {
    this.spaceService
      .saveTranscript(spaceId, transcript, lang, metadata, sessionId, breakoutRoomId)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  private onSpeechStart(e: Event): void {
    this.webspeechSpeechStartTimestamp = new Date();
    if (this.audioTrack) {
      this.startAudioGainAnalyser(this.audioTrack);
      this.startActiveSpeakerCounter();
    }
  }

  private onSpeechEnd(e: Event): void {
    this.webSpeechSpeechEndTimestamo = new Date();
    // Average microphone gain for webspeech start -> end interval
    if (this.gainTimeInterval) {
      const currentGains = this.userSpeechGainValues.splice(0, this.userSpeechGainValues.length);
      this.userSpeechGainValues = [];

      const averageMicrophoneGain = currentGains.reduce((a, b) => a + b, 0) / currentGains.length;
      this.averageMicrophoneGain = averageMicrophoneGain;

      clearInterval(this.gainTimeInterval);
    }
    // % of time where user was listed as active according to daily active user
    // % of time where user was listed as active according to blue border logic
    if (this.activeSpeakerInterval) {
      const currentActiveSpeakerTracker = this.activeSpeakerTracker.splice(
        0,
        this.activeSpeakerTracker.length,
      );
      this.activeSpeakerTracker = [];
      const totalItems = currentActiveSpeakerTracker.length;
      const numberOfTimeUserWasActiveSpeaker = currentActiveSpeakerTracker.filter(
        (val) => val === 1,
      );

      const dailyActivePercentage = (numberOfTimeUserWasActiveSpeaker.length * 100) / totalItems;
      this.dailyActivePercentage = dailyActivePercentage;

      const currentActiveBlueBorderSpekerTracker = this.activeSpeakerBlueBorderTracker.splice(
        0,
        this.activeSpeakerBlueBorderTracker.length,
      );
      this.activeSpeakerBlueBorderTracker = [];
      const totalBlueBorderItems = currentActiveBlueBorderSpekerTracker.length;
      const numberOfBlueBorderTimeUserWasActiveSpeaker =
        currentActiveBlueBorderSpekerTracker.filter((val) => val === 1);

      const blueBorderActivePercentage =
        (numberOfBlueBorderTimeUserWasActiveSpeaker.length * 100) / totalBlueBorderItems;
      this.blueBorderActivePercentage = blueBorderActivePercentage;

      clearInterval(this.activeSpeakerInterval);
    }
  }

  private onSoundStart(event): void {
    this.webspeechSoundStartTimestamp = new Date();
  }

  private onSoundEnd(event): void {
    this.webSpeechSoundEndTimestamp = new Date();
  }

  private sendTranscriptInput(input: {
    'time-active-bb-pct': number;
    'time-active-daily-pct': number;
    'avg-mic-gain': number;
    'echo-detected': number;
    'sound-duration': number;
    'speech-duration': number;
    'speech-start-delay': number;
    'transcript-length': number;
  }) {
    const intercept = -1.12093859;
    const weights = [
      0.05687555, 0.00634591, 2.15585481, -0.70674127, 0.01443279, 0.01056134, -0.00712712,
      -0.02644595,
    ];
    const inputCols = [
      'time-active-bb-pct',
      'time-active-daily-pct',
      'avg-mic-gain',
      'echo-detected',
      'sound-duration',
      'speech-duration',
      'speech-start-delay',
      'transcript-length',
    ];
    let z = intercept;
    for (let i = 0; i < inputCols.length; i++) {
      z += weights[i] * input[inputCols[i]];
    }
    const sigmoid = 1 / (1 + Math.exp(-z));
    return sigmoid;
  }

  private startActiveSpeakerCounter(): void {
    this.ngZone.runOutsideAngular(() => {
      this.activeSpeakerInterval = modifiedSetInterval(() => {
        if (this.currentActiveSpeaker?.speakerId === this.providerState.localParticipantIdValue) {
          this.activeSpeakerTracker.push(1);
        } else {
          this.activeSpeakerTracker.push(0);
        }
        if (
          this.sessionSharedDataService.getBlueBorderActiveSpeakerVal() ===
          this.providerState.localParticipantIdValue
        ) {
          this.activeSpeakerBlueBorderTracker.push(1);
        } else {
          this.activeSpeakerBlueBorderTracker.push(0);
        }
      }, 100);
    });
  }

  private listenToCurrentUserChange(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => (this.user = res?.user));
  }

  private startAudioGainAnalyser(audioTrack: MediaStreamTrack): void {
    const context = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new context();
    const audioAnalyzer = audioContext.createAnalyser();
    audioAnalyzer.smoothingTimeConstant = 0.3;
    audioAnalyzer.fftSize = 32;
    const audioStream = new MediaStream([audioTrack]);
    const source = audioContext.createMediaStreamSource(audioStream);
    source.connect(audioAnalyzer);
    const gainNode = audioContext.createGain();
    source.connect(gainNode);
    const frequencyData = new Uint8Array(audioAnalyzer.frequencyBinCount);
    audioAnalyzer.getByteFrequencyData(frequencyData);
    this.ngZone.runOutsideAngular(() => {
      this.gainTimeInterval = modifiedSetInterval(() => {
        audioAnalyzer.getByteFrequencyData(frequencyData);
        const dataArray = new Float32Array(audioAnalyzer.fftSize);
        audioAnalyzer.getFloatTimeDomainData(dataArray);
        const rms = Math.sqrt(
          Array.from(dataArray).reduce((acc, val) => acc + val * val, 0) / dataArray.length,
        );
        const gainValue = Math.max(0, Math.min(1, rms / 0.1));
        this.userSpeechGainValues.push(gainValue);
      }, 100);
    });
  }

  private listenToRtcActiveSpeaker(): void {
    this.providerState.currentActiveSpeaker$
      .pipe(untilDestroyed(this))
      .subscribe((x) => (this.currentActiveSpeaker = x));
  }

  private setFeatureFlags() {
    this.isBreakoutRoomsEnabled = this.flagsService.isFlagEnabled(FLAGS.BREAKOUT_ROOMS);

    this.isTranscriptionLoggingEnabled = this.flagsService.isFlagEnabled(
      FLAGS.ENABLE_TRANSCRIPTION_LOGGING,
    );

    this.flagsService
      .featureFlagChanged(FLAGS.ENABLE_TRANSCRIPTION_LOGGING)
      .pipe(untilDestroyed(this))
      .subscribe((val) => (this.isTranscriptionLoggingEnabled = val));

    this.flagsService
      .featureFlagChanged(FLAGS.BREAKOUT_ROOMS)
      .pipe(untilDestroyed(this))
      .subscribe((val) => (this.isBreakoutRoomsEnabled = val));
  }

  public onServiceTypeChange(speechServiceType: SpeechService): void {
    if (speechServiceType !== this.currentSpeechService && this.currentSpeechService) {
      this.stop(this.currentSpeechService);
      this.init(speechServiceType);
      this.transcriptRepo.updateTranscriptionSpeechService(this.currentSpeechService);
    }
  }
}
